import React, { useMemo, useState, useTransition } from "react";
import { Breadcrumb, Col, Empty, Pagination, Row, Spin } from "antd";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
} from "~/components/Breadcrumb";
import { TableHeaderBar, TableHeaderInfo } from "~/components/Tables/TableHeaderBar";
import { useSearchParams } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import type { UnitListFilters } from "~/types/unit";
import type { ListingBrowsePageLoaderResult } from "~/types/listing";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import {
  countActiveListingFilters,
  getFilterLocations,
  mergeListingFiltersIntoSearchParams,
  parseListingFiltersFromSearchParams,
  withFilterLocations,
} from "~/utils/listingFilterUrl";
import { ListingFilterDrawer, ListingFilterTriggerButton } from "./ListingFilterDrawer";
import { LocationSearchBar } from "./LocationSearchBar";
import { PlanListingCardView, UnitListingCardView } from "./ListingCard";

export type ListingBrowseViewProps = {
  loaderData: ListingBrowsePageLoaderResult;
};

export function ListingBrowseView({ loaderData }: ListingBrowseViewProps) {
  const { t } = useAppTranslation();
  const { isTableLoading } = useRouteActivity();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [isFilterPending, startFilterTransition] = useTransition();

  const lockedPropertyId = loaderData.lockedPropertyId;

  const filters = useMemo(() => {
    const parsed = parseListingFiltersFromSearchParams(searchParams);
    if (lockedPropertyId) {
      return { ...parsed, propertyId: lockedPropertyId };
    }
    return parsed;
  }, [searchParams, lockedPropertyId]);

  const activeFilterCount = useMemo(
    () =>
      countActiveListingFilters(filters, {
        excludePropertyId: !!lockedPropertyId,
        // Location is set from the home search bar, not the drawer.
        excludeLocation: true,
      }),
    [filters, lockedPropertyId]
  );

  const items = loaderData.items ?? [];
  const total = loaderData.total ?? 0;
  const page = loaderData.page ?? 1;
  const pageSize = loaderData.pageSize ?? 10;

  const title = lockedPropertyId
    ? loaderData.propertyInfo
      ? t("Listing_property_available_units_title")
      : loaderData.propertyName
        ? t("Listing_property_browse_title", {
            propertyName: loaderData.propertyName,
          })
        : t("Listing_property_browse_title_fallback")
    : t("Listing_browse_title");

  const breadcrumbItems = useMemo(() => {
    if (lockedPropertyId) {
      return null;
    }
    return [breadcrumbHomeItem, breadcrumbItem(t("Listing_browse_title"))];
  }, [lockedPropertyId, t]);

  const locationTerms = useMemo(
    () => getFilterLocations(filters),
    [filters]
  );

  const advancedFiltersFromUrl = useMemo(() => {
    const { locations: _locations, location: _location, propertyId: _propertyId, ...rest } =
      filters;
    return rest;
  }, [filters]);

  const handleLocationSearch = (
    locations: string[],
    advancedFilters: UnitListFilters
  ) => {
    startFilterTransition(() => {
      const merged = withFilterLocations(
        {
          ...advancedFilters,
          ...(lockedPropertyId ? { propertyId: lockedPropertyId } : {}),
        },
        locations
      );
      const nextParams = mergeListingFiltersIntoSearchParams(
        searchParams,
        merged
      );
      nextParams.delete("page");
      setSearchParams(nextParams);
    });
  };

  const handleApplyFilters = (next: UnitListFilters) => {
    startFilterTransition(() => {
      const merged: UnitListFilters = lockedPropertyId
        ? { ...next, propertyId: lockedPropertyId }
        : next;
      const nextParams = mergeListingFiltersIntoSearchParams(
        searchParams,
        merged
      );
      nextParams.delete("page");
      setSearchParams(nextParams);
    });
  };

  const handlePageChange = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams.toString());
    if (nextPage <= 1) {
      nextParams.delete("page");
    } else {
      nextParams.set("page", String(nextPage));
    }
    setSearchParams(nextParams);
  };

  return (
    <>
      {breadcrumbItems ? (
        <Breadcrumb separator=">" items={breadcrumbItems} />
      ) : null}
      <div style={{ marginTop: breadcrumbItems ? "2rem" : undefined }}>
      {!lockedPropertyId ? (
        <div style={{ marginBottom: 24 }}>
          <LocationSearchBar
            mode="inline"
            initialLocations={locationTerms}
            initialAdvancedFilters={advancedFiltersFromUrl}
            onSearch={handleLocationSearch}
            searchLoading={isFilterPending}
          />
        </div>
      ) : null}
      <ListingFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onApply={handleApplyFilters}
        applyLoading={isFilterPending}
      />
      <TableHeaderBar
        left={
          <TableHeaderInfo
            title={title}
            count={total}
            countLabel={t("Total_Records_Found")}
          />
        }
        right={
          lockedPropertyId ? (
            <ListingFilterTriggerButton
              activeCount={activeFilterCount}
              onOpen={() => setFilterDrawerOpen(true)}
            />
          ) : null
        }
      />
      <Spin spinning={isTableLoading || isFilterPending}>
        {total === 0 ? (
          <Empty
            description={t("Listing_browse_empty")}
            style={{ marginTop: "3rem" }}
          />
        ) : (
          <>
            <Row gutter={[16, 16]} style={{ marginTop: "1rem" }}>
              {items.map((listing) => (
                <Col
                  key={`${listing.kind}-${listing.id}`}
                  xs={24}
                  sm={12}
                  lg={8}
                >
                  {listing.kind === "plan" ? (
                    <PlanListingCardView card={listing} />
                  ) : (
                    <UnitListingCardView card={listing} />
                  )}
                </Col>
              ))}
            </Row>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: 24,
              }}
            >
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </Spin>
      </div>
    </>
  );
}
